import React, { useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  FaPlus,
  FaBoxOpen,
  FaTruck,
  FaCheckCircle,
  FaRegCopy,
  FaExternalLinkAlt,
  FaPaperclip,
  FaEdit,
  FaTrashAlt,
  FaUser,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaMoneyBillWave,
} from 'react-icons/fa';
import ShipmentModal from './ShipmentModal';
import {
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_OPTIONS,
  formatShipmentCost,
  formatShipmentDate,
  getCarrierLabel,
  getShipmentStatus,
  getTrackingUrl,
  summarizeShipments,
  todayInputValue,
} from '../../utils/shipments';
import styles from './ShipmentTracking.module.css';

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Ziyaret modalindeki "Kargo Takip" sekmesi.
 * Kayıtlar Visits/{visitId}.shipments dizisinde tutulur; her değişiklik
 * anında yazılır (Stok sekmesiyle aynı davranış).
 */
const ShipmentTracking = ({
  visitId,
  shipments = [],
  onShipmentsChange,
  isReadOnly = false,
  currentUserName = '',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const summary = useMemo(() => summarizeShipments(shipments), [shipments]);

  // En yeni kayıt üstte: önce kargoya veriliş tarihi, yoksa oluşturulma zamanı
  const sortedShipments = useMemo(
    () =>
      [...shipments].sort((a, b) => {
        const aDate = new Date(a.shippedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b.shippedAt || b.createdAt || 0).getTime();
        return bDate - aDate;
      }),
    [shipments]
  );

  const persist = async (nextShipments, successMessage) => {
    if (!visitId) {
      toast.error('Ziyaret bilgisi bulunamadı');
      throw new Error('missing visit id');
    }
    try {
      await updateDoc(doc(db, 'Visits', visitId), {
        shipments: nextShipments,
        lastUpdated: new Date().toISOString(),
      });
      onShipmentsChange(nextShipments);
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      console.error('Error saving shipments:', error);
      toast.error('Kargo kaydı kaydedilirken bir hata oluştu');
      throw error;
    }
  };

  const handleSave = async (formData) => {
    if (editingShipment) {
      const next = shipments.map((item) =>
        item.id === editingShipment.id
          ? { ...item, ...formData, updatedAt: new Date().toISOString() }
          : item
      );
      await persist(next, 'Kargo kaydı güncellendi');
    } else {
      const record = {
        ...formData,
        id: newId(),
        createdAt: new Date().toISOString(),
        createdBy: currentUserName,
      };
      await persist([...shipments, record], 'Kargo kaydı eklendi');
    }
  };

  // Kart üzerindeki hızlı işlem: bir sonraki duruma geçir
  const handleQuickStatus = async (shipment, status) => {
    setBusyId(shipment.id);
    try {
      const next = shipments.map((item) => {
        if (item.id !== shipment.id) return item;
        const updated = { ...item, status, updatedAt: new Date().toISOString() };
        if (status !== SHIPMENT_STATUS.NOT_SENT && !updated.shippedAt) {
          updated.shippedAt = todayInputValue();
        }
        if (status === SHIPMENT_STATUS.DELIVERED && !updated.deliveredAt) {
          updated.deliveredAt = todayInputValue();
        }
        return updated;
      });
      await persist(next, `Durum "${getShipmentStatus(status).label}" olarak güncellendi`);
    } catch (error) {
      // persist zaten bildirdi
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (shipment) => {
    const confirmed = window.confirm(
      `"${shipment.subject}" kargo kaydı silinsin mi? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;
    setBusyId(shipment.id);
    try {
      await persist(
        shipments.filter((item) => item.id !== shipment.id),
        'Kargo kaydı silindi'
      );
    } catch (error) {
      // persist zaten bildirdi
    } finally {
      setBusyId(null);
    }
  };

  const copyTracking = (trackingNumber) => {
    navigator.clipboard
      ?.writeText(trackingNumber)
      .then(() => toast.success('Takip numarası kopyalandı'))
      .catch(() => toast.error('Kopyalanamadı'));
  };

  const openNew = () => {
    setEditingShipment(null);
    setShowModal(true);
  };

  const openEdit = (shipment) => {
    setEditingShipment(shipment);
    setShowModal(true);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <FaBoxOpen className={styles.summaryIcon} />
            <span className={styles.summaryValue}>{summary.total}</span>
            <span className={styles.summaryLabel}>Toplam</span>
          </div>
          <div className={`${styles.summaryItem} ${styles.tone_neutral}`}>
            <span className={styles.summaryValue}>{summary[SHIPMENT_STATUS.NOT_SENT]}</span>
            <span className={styles.summaryLabel}>Verilmedi</span>
          </div>
          <div className={`${styles.summaryItem} ${styles.tone_info}`}>
            <FaTruck className={styles.summaryIcon} />
            <span className={styles.summaryValue}>{summary[SHIPMENT_STATUS.SHIPPED]}</span>
            <span className={styles.summaryLabel}>Kargoda</span>
          </div>
          <div className={`${styles.summaryItem} ${styles.tone_success}`}>
            <FaCheckCircle className={styles.summaryIcon} />
            <span className={styles.summaryValue}>{summary[SHIPMENT_STATUS.DELIVERED]}</span>
            <span className={styles.summaryLabel}>Teslim</span>
          </div>
          {summary.totalCost > 0 && (
            <div className={styles.summaryItem}>
              <FaMoneyBillWave className={styles.summaryIcon} />
              <span className={styles.summaryValue}>{formatShipmentCost(summary.totalCost)}</span>
              <span className={styles.summaryLabel}>Kargo Bedeli</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className={styles.primaryButton}
          onClick={openNew}
          disabled={isReadOnly}
        >
          <FaPlus /> Kargo Ekle
        </button>
      </div>

      {sortedShipments.length === 0 ? (
        <div className={styles.emptyState}>
          <FaBoxOpen className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>Henüz kargo kaydı yok</p>
          <p className={styles.emptyHint}>
            Bu ziyaret için gönderilen malzemeleri, takip numarasını ve kargo belgelerini
            burada takip edebilirsiniz.
          </p>
          {!isReadOnly && (
            <button type="button" className={styles.primaryButton} onClick={openNew}>
              <FaPlus /> İlk kaydı ekle
            </button>
          )}
        </div>
      ) : (
        <div className={styles.cardList}>
          {sortedShipments.map((shipment) => {
            const status = getShipmentStatus(shipment.status);
            const trackingUrl = getTrackingUrl(shipment.carrier, shipment.trackingNumber);
            const carrierLabel = getCarrierLabel(shipment.carrier);
            const shippedAt = formatShipmentDate(shipment.shippedAt);
            const deliveredAt = formatShipmentDate(shipment.deliveredAt);
            const cost = formatShipmentCost(shipment.cost);
            const attachments = shipment.attachments || [];
            const isBusy = busyId === shipment.id;

            return (
              <article
                key={shipment.id}
                className={`${styles.card} ${styles[`cardTone_${status.tone}`]}`}
              >
                <header className={styles.cardHeader}>
                  <div className={styles.cardTitleGroup}>
                    <span className={`${styles.statusBadge} ${styles[`tone_${status.tone}`]}`}>
                      {status.label}
                    </span>
                    <h4 className={styles.cardTitle}>{shipment.subject}</h4>
                  </div>
                  {!isReadOnly && (
                    <div className={styles.cardActions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => openEdit(shipment)}
                        title="Düzenle"
                        disabled={isBusy}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                        onClick={() => handleDelete(shipment)}
                        title="Sil"
                        disabled={isBusy}
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  )}
                </header>

                {shipment.content && <p className={styles.cardContent}>{shipment.content}</p>}

                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <FaUser className={styles.metaIcon} />
                    <span>
                      {shipment.recipientName}
                      {shipment.recipientOrg ? ` · ${shipment.recipientOrg}` : ''}
                    </span>
                  </div>
                  {shipment.destination && (
                    <div className={styles.metaItem}>
                      <FaMapMarkerAlt className={styles.metaIcon} />
                      <span>{shipment.destination}</span>
                    </div>
                  )}
                  {shipment.recipientPhone && (
                    <div className={styles.metaItem}>
                      <FaPhoneAlt className={styles.metaIcon} />
                      <a href={`tel:${shipment.recipientPhone}`} className={styles.metaLink}>
                        {shipment.recipientPhone}
                      </a>
                    </div>
                  )}
                  {(carrierLabel || shippedAt || deliveredAt) && (
                    <div className={styles.metaItem}>
                      <FaTruck className={styles.metaIcon} />
                      <span>
                        {[
                          carrierLabel,
                          shippedAt ? `Gönderim: ${shippedAt}` : null,
                          deliveredAt ? `Teslim: ${deliveredAt}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                  )}
                  {cost && (
                    <div className={styles.metaItem}>
                      <FaMoneyBillWave className={styles.metaIcon} />
                      <span>
                        Kargo bedeli: <strong>{cost}</strong>
                      </span>
                    </div>
                  )}
                </div>

                {shipment.trackingNumber && (
                  <div className={styles.trackingRow}>
                    <span className={styles.trackingLabel}>Takip No</span>
                    <code className={styles.trackingNumber}>{shipment.trackingNumber}</code>
                    <button
                      type="button"
                      className={styles.trackingAction}
                      onClick={() => copyTracking(shipment.trackingNumber)}
                      title="Takip numarasını kopyala"
                    >
                      <FaRegCopy />
                    </button>
                    {trackingUrl && (
                      <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.trackingAction}
                        title={`${carrierLabel} sitesinde sorgula`}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    )}
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className={styles.attachmentRow}>
                    <FaPaperclip className={styles.metaIcon} />
                    {attachments.map((file, index) => (
                      <a
                        key={file.storagePath || `${file.name}-${index}`}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentChip}
                        title={file.name}
                      >
                        {file.name}
                      </a>
                    ))}
                  </div>
                )}

                {shipment.note && <p className={styles.cardNote}>{shipment.note}</p>}

                {!isReadOnly && (
                  <footer className={styles.cardFooter}>
                    {SHIPMENT_STATUS_OPTIONS.filter(
                      (option) => option.value !== shipment.status
                    ).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`${styles.quickAction} ${styles[`quick_${option.tone}`]}`}
                        onClick={() => handleQuickStatus(shipment, option.value)}
                        disabled={isBusy}
                      >
                        {option.label} olarak işaretle
                      </button>
                    ))}
                  </footer>
                )}
              </article>
            );
          })}
        </div>
      )}

      <ShipmentModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingShipment(null);
        }}
        visitId={visitId}
        shipment={editingShipment}
        onSave={handleSave}
        defaultSender={currentUserName}
      />
    </div>
  );
};

export default ShipmentTracking;
